import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, XCircle, CheckCircle, AlertTriangle, FileText, Settings, Users } from 'lucide-react';
import api from '../../../utils/api';
import MainLayout from '../../../components/layout/MainLayout';
import { io } from 'socket.io-client';
import { useAuth } from '../../../context/AuthContext';

const WWebBulkJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJob();

    // Set up Socket.IO for real-time updates
    const socketUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';
    const socket = io(socketUrl);
    
    if (user?.tenantId) {
      socket.emit('join_tenant', user.tenantId);
    }

    socket.on('wa_web_bulk_progress', ({ jobId, progress }) => {
      if (jobId === id) {
        setJob(prev => prev ? { ...prev, progress } : prev);
      }
    });

    socket.on('wa_web_bulk_complete', ({ jobId, finalProgress }) => {
      if (jobId === id) {
        setJob(prev => prev ? { ...prev, status: 'completed', progress: finalProgress } : prev);
      }
    });

    socket.on('wa_web_bulk_paused', ({ jobId }) => {
      if (jobId === id) {
        setJob(prev => prev ? { ...prev, status: 'paused' } : prev);
      }
    });

    socket.on('wa_web_bulk_resumed', ({ jobId }) => {
        if (jobId === id) {
          setJob(prev => prev ? { ...prev, status: 'pending' } : prev);
        }
      });

    socket.on('wa_web_bulk_cancelled', ({ jobId }) => {
      if (jobId === id) {
        setJob(prev => prev ? { ...prev, status: 'cancelled' } : prev);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [id, user]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const res = await api(`/wa-web-automation/jobs/${id}`);
      if (res.ok) {
        const data = await res.json();
        setJob(data);
      }
    } catch (err) {
      console.error('Failed to fetch job:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    try {
      const res = await api(`/wa-web-automation/jobs/${id}/${action}`, { method: 'PATCH' });
      if (res.ok) {
        fetchJob();
      } else {
        const data = await res.json();
        alert(data.message || `Failed to ${action} job`);
      }
    } catch (err) {
      alert(`Error trying to ${action} job`);
    }
  };

  if (loading) {
    return <MainLayout><div style={{ padding: '40px', textAlign: 'center' }}>Loading campaign details...</div></MainLayout>;
  }

  if (!job) {
    return <MainLayout><div style={{ padding: '40px', textAlign: 'center' }}>Campaign not found.</div></MainLayout>;
  }

  const progressPct = job.leadCount > 0 ? Math.round((job.progress.processed / job.leadCount) * 100) : 0;
  
  const getStatusBadge = () => {
    switch (job.status) {
      case 'running': return <span style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Running</span>;
      case 'paused': return <span style={{ background: '#fef3c7', color: '#92400e', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Paused</span>;
      case 'completed': return <span style={{ background: '#dcfce7', color: '#166534', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Completed</span>;
      case 'cancelled': return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Cancelled</span>;
      case 'failed': return <span style={{ background: '#fee2e2', color: '#991b1b', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Failed</span>;
      default: return <span style={{ background: '#f3f4f6', color: '#374151', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: '500' }}>Pending</span>;
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div>
            <button 
              onClick={() => navigate('/automation/whatsapp-web/bulk/jobs')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', padding: 0, fontSize: '14px', marginBottom: '16px' }}
            >
              <ArrowLeft size={16} /> Back to History
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold' }}>{job.templateName}</h1>
              {getStatusBadge()}
            </div>
            <div style={{ color: 'var(--color-text-tertiary)', fontSize: '14px', marginTop: '8px' }}>
              Created on {new Date(job.createdAt).toLocaleString()} by {job.createdByName}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            {job.status === 'running' && (
              <button onClick={() => handleAction('pause')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                <Pause size={18} /> Pause
              </button>
            )}
            {job.status === 'paused' && (
              <button onClick={() => handleAction('resume')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#dcfce7', color: '#166534', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                <Play size={18} /> Resume
              </button>
            )}
            {['pending', 'running', 'paused'].includes(job.status) && (
              <button onClick={() => { if(window.confirm('Cancel campaign?')) handleAction('cancel'); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>
                <XCircle size={18} /> Cancel
              </button>
            )}
          </div>
        </div>

        {/* Progress Section */}
        <div style={{ background: 'white', padding: '32px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>Campaign Progress</h3>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                {job.status === 'running' 
                  ? `Processing batch ${job.progress.currentBatch} of ${job.progress.totalBatches}` 
                  : `Processed ${job.progress.processed} of ${job.leadCount} leads`}
              </p>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-brand-primary)' }}>
              {progressPct}%
            </div>
          </div>

          <div style={{ width: '100%', height: '12px', background: 'var(--color-background-secondary)', borderRadius: '6px', overflow: 'hidden', marginBottom: '32px' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', background: 'var(--color-brand-primary)', transition: 'width 0.5s ease-out' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            <div style={{ background: 'var(--color-background-primary)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
              <Users size={24} color="var(--color-text-primary)" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{job.leadCount}</div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Leads</div>
            </div>
            <div style={{ background: '#dcfce7', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
              <CheckCircle size={24} color="#166534" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#166534' }}>{job.progress.successCount}</div>
              <div style={{ fontSize: '12px', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sent Successfully</div>
            </div>
            <div style={{ background: '#fee2e2', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
              <XCircle size={24} color="#991b1b" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#991b1b' }}>{job.progress.failCount}</div>
              <div style={{ fontSize: '12px', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Failed</div>
            </div>
            <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
              <AlertTriangle size={24} color="#92400e" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#92400e' }}>{job.progress.skippedCount}</div>
              <div style={{ fontSize: '12px', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Skipped (Invalid No.)</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Message Snapshot */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={20} /> Template Used
            </h3>
            <div style={{ background: 'var(--color-background-secondary)', padding: '16px', borderRadius: '8px', whiteSpace: 'pre-wrap', fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
              {job.templateSnapshot}
            </div>
            
            {job.variableMappings && job.variableMappings.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Variable Mappings:</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                  {job.variableMappings.map(v => (
                    <li key={v.placeholder} style={{ marginBottom: '4px' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>{`{{${v.placeholder}}}`}</strong> → 
                      {v.source === 'lead_field' ? ` Lead Field: ${v.leadField}` : ` Static: ${v.staticValue}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Anti-Ban Settings Log */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Settings size={20} /> Anti-Ban Configuration
            </h3>
            
            <table style={{ width: '100%', fontSize: '14px' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                  <td style={{ padding: '12px 0', color: 'var(--color-text-secondary)' }}>Batch Size</td>
                  <td style={{ padding: '12px 0', fontWeight: '500', textAlign: 'right' }}>{job.batchSize} messages</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                  <td style={{ padding: '12px 0', color: 'var(--color-text-secondary)' }}>Message Delay</td>
                  <td style={{ padding: '12px 0', fontWeight: '500', textAlign: 'right' }}>{job.delayMinMs/1000}s - {job.delayMaxMs/1000}s</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                  <td style={{ padding: '12px 0', color: 'var(--color-text-secondary)' }}>Batch Cooldown</td>
                  <td style={{ padding: '12px 0', fontWeight: '500', textAlign: 'right' }}>{job.cooldownBetweenBatchesMs/60000} minutes</td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 0', color: 'var(--color-text-secondary)' }}>Typing Simulation</td>
                  <td style={{ padding: '12px 0', fontWeight: '500', textAlign: 'right' }}>{job.simulateTyping ? 'Enabled' : 'Disabled'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </MainLayout>
  );
};

export default WWebBulkJobDetail;
